"use client";

import Link from "next/link";
import { useParams } from "next/navigation";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import "../../terminal.css";

type Asset = "stock" | "etf" | "fund" | "futures" | "bond";
type Product = { code: string; name: string; market: string; quoteId: string; classify: string };
type Quote = { code: string; name: string; price: number; high: number; low: number; open: number; prevClose: number; changePct: number; updatedAt: string; turnoverRate?: number; marketCap?: number; peDynamic?: number; noTrade?: boolean; delayed?: boolean; source?: string };
type Bar = { time: string; open: number; close: number; high: number; low: number; volume: number };
const compactMarketValue = (value?: number) => !Number.isFinite(value) ? "—" : value! >= 1e12 ? (value! / 1e12).toFixed(2) + "万亿" : value! >= 1e8 ? (value! / 1e8).toFixed(2) + "亿" : value!.toLocaleString("zh-CN");

const configurations = {
  stock: { label: "A股", icon: "股", unit: "100股/手", settle: "T+1", placeholder: "输入股票代码、名称或拼音", example: "例如 600519 / 贵州茅台" },
  etf: { label: "ETF", icon: "基", unit: "100份/手", settle: "自动识别", placeholder: "输入ETF代码、名称或拼音", example: "例如 510300 / 沪深300ETF" },
  fund: { label: "\u57fa\u91d1", icon: "\u91d1", unit: "100\u4efd/\u624b", settle: "T+1", placeholder: "\u8f93\u5165\u57fa\u91d1\u4ee3\u7801\u3001\u540d\u79f0\u6216\u62fc\u97f3", example: "\u4f8b\u5982 161725 / \u767d\u9152LOF" },
  futures: { label: "期货", icon: "期", unit: "1手", settle: "T+0", placeholder: "输入期货代码、名称或拼音", example: "例如 IF2608 / 螺纹钢2610" },
  bond: { label: "债券", icon: "债", unit: "1手", settle: "T+0", placeholder: "输入债券代码、名称或拼音", example: "例如 国债 / 019547" },
} as const;

function beijingParts() {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Shanghai", hour12: false, weekday: "short", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date());
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
}

function isOpen(asset: Asset) {
  const p = beijingParts();
  if (p.weekday === "Sat" || p.weekday === "Sun") return false;
  const minute = +p.hour * 60 + +p.minute;
  if (asset === "futures") {
    return (minute >= 540 && minute <= 615) || (minute >= 630 && minute <= 690) || (minute >= 810 && minute <= 900) || minute >= 1260 || minute <= 150;
  }
  return (minute >= 570 && minute <= 690) || (minute >= 780 && minute <= 900);
}

function etfRule(name: string) {
  return /跨境|QDII|纳指|标普|恒生|港股|日经|德国|法国|黄金|货币|现金|债/.test(name) ? "T+0" : "T+1";
}

function CandleChart({ bars, loading, range, emptyText="暂无该周期数据" }: { bars: Bar[]; loading: boolean; range: string; emptyText?: string }) {
  const windowSize = 80;
  const [endIndex, setEndIndex] = useState(bars.length);
  const [fullscreen, setFullscreen] = useState(false);
  const drag = useRef<{ x: number; end: number } | null>(null);
  const previousLength = useRef(0);
  useEffect(() => { const wasAtLatest = endIndex === 0 || endIndex >= previousLength.current; previousLength.current = bars.length; setEndIndex((current) => wasAtLatest ? bars.length : Math.min(current, bars.length)); }, [bars.length]);
  useEffect(() => {
    if (!fullscreen) return;
    const previous = document.body.style.overflow; document.body.style.overflow = "hidden";
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", escape);
    return () => { document.body.style.overflow = previous; window.removeEventListener("keydown", escape); };
  }, [fullscreen]);
  if (loading) return <div className="chart-empty chart-loading"><i/><b>正在加载行情图表…</b><span>正在连接行情数据源</span></div>;
  if (!bars.length) return <div className="chart-empty">{emptyText}</div>;

  const width = 920, height = 390, left = 18, right = 78, top = 20, bottom = 42;
  const count = Math.min(windowSize, bars.length);
  const safeEnd = Math.min(bars.length, Math.max(count, endIndex));
  const startIndex = Math.max(0, safeEnd - count);
  const data = bars.slice(startIndex, safeEnd);
  const scaleData = fullscreen ? bars : data;
  const rawMax = Math.max(...scaleData.map((b) => b.high)), rawMin = Math.min(...scaleData.map((b) => b.low));
  const padding = Math.max((rawMax - rawMin) * .035, Math.abs(rawMax || 1) * .002);
  const max = rawMax + padding, min = rawMin - padding, span = max - min || 1;
  const plotWidth = width - left - right, plotHeight = height - top - bottom, step = plotWidth / Math.max(1, data.length);
  const y = (v: number) => top + (max - v) / span * plotHeight;
  const labelFractions = fullscreen ? [0, .16, .33, .5, .67, .84, 1] : [0, .25, .5, .75, 1];
  const xLabels = [...new Set(labelFractions.map((fraction) => Math.floor((data.length - 1) * fraction)))];
  const isMultiDay = new Set(data.map((bar) => bar.time.trim().split(/\s+/)[0])).size > 1;
  const formatXTime = (value: string) => {
    if (range !== "hour") return value.includes(" ") ? value.trim().split(/\s+/).at(-1) || value : value.slice(2).replace(/-/g, "/");
    const [datePart = "", timePart = ""] = value.trim().split(/\s+/), digits = datePart.replace(/\D/g, "");
    const dateLabel = digits.length >= 8 ? `${digits.slice(-4, -2)}/${digits.slice(-2)}` : datePart.slice(-5).replace("-", "/");
    return isMultiDay ? `${dateLabel} ${timePart.slice(0, 5)}` : timePart.slice(0, 5);
  };
  const move = (clientX: number) => {
    if (!drag.current) return;
    const sensitivity = fullscreen ? 2.2 : 1;
    const shift = Math.round((drag.current.x - clientX) * count / plotWidth * sensitivity);
    setEndIndex(Math.min(bars.length, Math.max(count, drag.current.end + shift)));
  };
  return <div className={`chart-interactive ${fullscreen ? "is-fullscreen" : ""}`}>
    <div className="chart-tools"><span>按住图表左右拖动查看历史</span><div><button onClick={() => setEndIndex(bars.length)}>回到最新</button><button className="fullscreen-button" onClick={() => setFullscreen((value) => { if (!value) setEndIndex(bars.length); return !value; })}>{fullscreen ? "退出全屏" : "全屏查看"}</button></div></div>
    <svg viewBox={`0 0 ${width} ${height}`} className="candle-chart" role="img" aria-label="可左右拖动的K线图"
      onPointerDown={(e) => { drag.current = { x: e.clientX, end: safeEnd }; e.currentTarget.setPointerCapture(e.pointerId); }}
      onPointerMove={(e) => move(e.clientX)} onPointerUp={(e) => { drag.current = null; e.currentTarget.releasePointerCapture(e.pointerId); }}
      onPointerCancel={() => { drag.current = null; }}
      onWheel={(e) => {
        if (!fullscreen) return;
        e.preventDefault();
        const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
        const delta = horizontal ? e.deltaX : -e.deltaY;
        const distance = Math.max(3, Math.round(Math.abs(horizontal ? e.deltaX : e.deltaY) / 7));
        const direction = delta > 0 ? 1 : -1;
        setEndIndex((current) => Math.min(bars.length, Math.max(count, current + direction * distance)));
      }}>
      <rect x={left} y={top} width={plotWidth} height={plotHeight} className="chart-hit" />
      <rect x={width-right} y={top} width={right} height={plotHeight} className="axis-gutter" />
      {[0, 1, 2, 3, 4].map((i) => { const py = top + i * plotHeight / 4, value = max - i * span / 4; return <g key={`y-${i}`}><line x1={left} x2={width-right} y1={py} y2={py} className="grid-line"/><text x={width-right+9} y={py+4} className="axis-label">{value.toFixed(value >= 100 ? 2 : 3)}</text></g>; })}
      {xLabels.map((index) => { const px = left + index * step + step / 2; return <g key={`x-${index}`}><line x1={px} x2={px} y1={top} y2={height-bottom} className="grid-line vertical"/><text x={px} y={height-13} textAnchor="middle" className="axis-label">{formatXTime(data[index]?.time || "")}</text></g>; })}
      {data.map((b, i) => { const x = left + i * step + step / 2, up = b.close >= b.open, bodyTop = y(Math.max(b.open, b.close)), body = Math.max(1.5, Math.abs(y(b.open)-y(b.close))); return <g key={`${b.time}-${i}`} className={up ? "candle-up" : "candle-down"}><line x1={x} x2={x} y1={y(b.high)} y2={y(b.low)}/><rect x={x-Math.max(1,step*.28)} y={bodyTop} width={Math.max(2,step*.56)} height={body}/></g>; })}
      <line x1={left} x2={width-right} y1={height-bottom} y2={height-bottom} className="axis-line"/><line x1={width-right} x2={width-right} y1={top} y2={height-bottom} className="axis-line"/>
    </svg>
  </div>;
}
export default function MarketTerminal() {
  const rawAsset = useParams<{ asset: string }>().asset;
  const asset: Asset = (["stock", "etf", "fund", "futures", "bond"].includes(rawAsset) ? rawAsset : "stock") as Asset;
  const config = configurations[asset];
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState<Product[]>([]);
  const [selected, setSelected] = useState<Product | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteCheckedAt, setQuoteCheckedAt] = useState("");
  const [chartCheckedAt, setChartCheckedAt] = useState("");
  const [bars, setBars] = useState<Bar[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [range, setRange] = useState("day");
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [message, setMessage] = useState("");
  const [watched, setWatched] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const open = isOpen(asset);
  const settlement = asset === "etf" && selected ? etfRule(selected.name) : config.settle;

  function loadRecentSearches() { try { const all = JSON.parse(localStorage.getItem("hongpan-search-history-v1") || "{}"); setRecentSearches(Array.isArray(all[asset]) ? all[asset].slice(0, 5) : []); } catch { setRecentSearches([]); } }
  function rememberSearch(value: string) { const term=value.trim(); if(!term)return; let all:Record<string,string[]>={}; try { all=JSON.parse(localStorage.getItem("hongpan-search-history-v1")||"{}"); } catch {} const next=[term,...(all[asset]||[]).filter(item=>item.toLowerCase()!==term.toLowerCase())].slice(0,5); all[asset]=next; localStorage.setItem("hongpan-search-history-v1",JSON.stringify(all)); setRecentSearches(next); }
  function deleteRecentSearch(value: string) { let all:Record<string,string[]>={}; try { all=JSON.parse(localStorage.getItem("hongpan-search-history-v1")||"{}"); } catch {} const next=(all[asset]||[]).filter(item=>item!==value).slice(0,5); all[asset]=next; localStorage.setItem("hongpan-search-history-v1",JSON.stringify(all)); setRecentSearches(next); }

  async function search(e?: FormEvent) {
    e?.preventDefault();
    if (!keyword.trim()) return;
    if (e) rememberSearch(keyword);
    setLoading(true);
    try {
      const response = await fetch(`/api/market/search?asset=${asset}&keyword=${encodeURIComponent(keyword.trim())}`);
      const json = await response.json();
      setResults(json.items || []);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    loadRecentSearches();
    setResults([]); setSelected(null); setQuote(null); setBars([]); setKeyword(""); setMessage("");
    const params = new URLSearchParams(window.location.search);
    const quoteId = params.get("secid"), code = params.get("code"), name = params.get("name");
    if (quoteId && code && name) setSelected({ quoteId, code, name, market: "", classify: "" });
  }, [asset]);

  useEffect(() => {
    const term = keyword.trim();
    if (!term) { setResults([]); return; }
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/market/search?asset=${asset}&keyword=${encodeURIComponent(term)}`);
        const json = await response.json();
        setResults(json.items || []);
      } finally { setLoading(false); }
    }, 320);
    return () => window.clearTimeout(timer);
  }, [keyword, asset]);

  useEffect(() => {
    if (!selected) { setWatched(false); return; }
    try {
      const list = JSON.parse(localStorage.getItem("hongpan-multi-watch-v1") || "[]");
      setWatched(list.some((item: Product & { asset: Asset }) => item.asset === asset && item.quoteId === selected.quoteId));
    } catch { setWatched(false); }
  }, [selected, asset]);

  useEffect(() => {
    if (!selected) return;
    let active = true;
    const update = async () => {
      try {
        const response = await fetch(`/api/market/quote?secid=${selected.quoteId}&_t=${Date.now()}`, { cache: "no-store", headers: { "Cache-Control": "no-cache" } });
        const json = await response.json();
        if (active && !json.error) {
          setQuote(json);
          setQuoteCheckedAt(new Date().toLocaleTimeString("zh-CN", { hour12: false }));
          if (range === "minute" || range === "hour") setBars((current) => {
            if (!current.length) return current;
            const next = [...current], last = next[next.length - 1];
            next[next.length - 1] = { ...last, close: json.price, high: Math.max(last.high, json.price), low: Math.min(last.low, json.price) };
            return next;
          });
        }
      } catch {}
    };
    update();
    const timer = window.setInterval(() => { if (isOpen(asset)) update(); }, 5000);
    return () => { active = false; window.clearInterval(timer); };
  }, [selected, asset, range]);

  useEffect(() => {
    if (!selected) { setChartLoading(false); return; }
    let active = true, firstLoad = true;
    const updateChart = async () => {
      if (firstLoad) { setBars([]); setChartLoading(true); }
      try {
        const response = await fetch(`/api/market/kline?secid=${selected.quoteId}&range=${range}&_t=${Date.now()}`, { cache: "no-store", headers: { "Cache-Control": "no-cache" } });
        const json = await response.json();
        if (active) {
          const raw: Bar[] = json.bars || [];
          const sessionBars = asset === "futures" ? raw : raw.filter((bar) => { const clock = bar.time.includes(" ") ? bar.time.split(" ")[1]?.slice(0, 5) : ""; return !clock || clock <= "15:00"; });
          setBars(sessionBars);
          setChartCheckedAt(new Date().toLocaleTimeString("zh-CN", { hour12: false }));
        }
      } catch { if (active && firstLoad) setBars([]); }
      finally { if (active && firstLoad) { firstLoad = false; setChartLoading(false); } }
    };
    updateChart();
    const timer = window.setInterval(() => { if (isOpen(asset)) updateChart(); }, 5000);
    return () => { active = false; window.clearInterval(timer); };
  }, [selected, range, asset]);

  const ruleNotes = useMemo(() => {
    if (asset === "stock") return ["买入证券当日不可卖出", "竞价 9:15–9:25；连续竞价 9:30–11:30、13:00–14:57；收盘集合竞价至 15:00", "涨跌停、停牌与最小申报单位随证券状态执行"];
    if (asset === "etf") return [`${settlement}：${settlement === "T+0" ? "当日买入可当日卖出" : "当日买入下一交易日可卖"}`, "普通股票 ETF 通常 T+1；跨境、黄金、债券、货币 ETF 通常 T+0", "最小 100 份，价格最小变动 0.001 元"];
    if (asset === "fund") return ["\u573a\u5185LOF\u3001\u5c01\u95ed\u5f0f\u57fa\u91d1\u7b49\u6309 T+1 \u56de\u8f6c\u4ea4\u6613", "\u7ade\u4ef7 9:15-9:25\uff1b\u8fde\u7eed\u4ea4\u6613 9:30-11:30\u300113:00-15:00", "\u6700\u5c0f 100 \u4efd\uff0c\u7533\u8d2d\u8d4e\u56de\u3001\u6298\u6ea2\u4ef7\u548c\u4ea4\u6613\u8d39\u7528\u6309\u57fa\u91d1\u5408\u540c\u4e0e\u4ea4\u6613\u6240\u89c4\u5219\u6267\u884c"];
    if (asset === "bond") return ["交易所债券支持 T+0 回转交易", "竞价 9:15–9:25；连续交易 9:30–11:30、13:00–15:00", "债券计价、申报单位及价格笼子按品种与交易所规则执行"];
    return ["T+0 双向交易，支持开多、开空与平仓", "按合约乘数、交易所保证金与涨跌停计算；本页展示模拟估算", "中金所主要为 9:30–11:30、13:00–15:00；商品期货夜盘结束时间因品种不同"];
  }, [asset, settlement]);

  function toggleWatch() {
    if (!selected) return;
    let list: Array<Product & { asset: Asset }> = [];
    try { list = JSON.parse(localStorage.getItem("hongpan-multi-watch-v1") || "[]"); } catch {}
    const exists = list.some((item) => item.asset === asset && item.quoteId === selected.quoteId);
    const next = exists ? list.filter((item) => !(item.asset === asset && item.quoteId === selected.quoteId)) : [{ ...selected, asset }, ...list].slice(0, 30);
    localStorage.setItem("hongpan-multi-watch-v1", JSON.stringify(next));
    setWatched(!exists);
  }

  function order(side: string) {
    const lot = asset === "stock" ? 100 : 1;
    if (!Number.isInteger(quantity) || quantity <= 0 || quantity % lot !== 0) return setMessage("\u586b\u5199\u65e0\u6548");
    if (!selected || !quote) return setMessage("\u8bf7\u5148\u9009\u62e9\u4ea7\u54c1\u5e76\u7b49\u5f85\u884c\u60c5");
    if (quote.noTrade) return setMessage("该债券当前暂无成交，仅展示发行/昨收参考价，不能按参考价模拟成交");
    if (!open) return setMessage("\u5f53\u524d\u4f11\u5e02\uff0c\u8ba2\u5355\u5165\u53e3\u5df2\u9501\u5b9a");
    const now = new Date(), time = now.toISOString();
    const orders = JSON.parse(localStorage.getItem("hongpan-orders-v1") || "[]");
    orders.unshift({ id: Date.now(), asset, code: selected.code, name: selected.name, side, quantity, price: quote.price, time, settlement });
    localStorage.setItem("hongpan-orders-v1", JSON.stringify(orders.slice(0, 200)));

    if (asset === "stock") {
      const state = JSON.parse(localStorage.getItem("hongpan-v3") || "null");
      if (!state?.profile) return setMessage("\u8bf7\u5148\u8fd4\u56de\u4e3b\u754c\u9762\u9009\u62e9\u521d\u59cb\u8d44\u91d1");
      const positions = Array.isArray(state.positions) ? state.positions : [];
      const current = positions.find((item: any) => item.code === selected.code);
      const amount = quote.price * quantity;
      const fee = state.light ? 0 : Math.max(5, amount * .00025) + (side === "\u5356\u51fa" ? amount * .0005 : 0) + amount * .00001;
      if (side === "\u4e70\u5165") {
        if (Number(state.cash || 0) < amount + fee) return setMessage("\u53ef\u7528\u73b0\u91d1\u4e0d\u8db3\uff0c\u65e0\u6cd5\u5b8c\u6210\u672c\u6b21\u6a21\u62df\u4e70\u5165");
        state.cash -= amount + fee;
        state.positions = current ? positions.map((item: any) => item.code === selected.code ? { ...item, shares: item.shares + quantity, cost: (item.cost * item.shares + amount) / (item.shares + quantity) } : item) : [...positions, { id: crypto.randomUUID(), code: selected.code, name: selected.name, shares: quantity, cost: quote.price }];
      } else {
        if (!current || current.shares < quantity) return setMessage("\u53ef\u5356\u6301\u4ed3\u4e0d\u8db3");
        state.cash = Number(state.cash || 0) + amount - fee;
        state.positions = positions.map((item: any) => item.code === selected.code ? { ...item, shares: item.shares - quantity } : item).filter((item: any) => item.shares > 0);
      }
      const trades = Array.isArray(state.trades) ? state.trades : [];
      state.trades = [{ id: crypto.randomUUID(), side, code: selected.code, name: selected.name, shares: quantity, price: quote.price, fee, time: now.toLocaleString("zh-CN", { hour12: false }) }, ...trades];
      localStorage.setItem("hongpan-v3", JSON.stringify(state));
    } else {
      const key = "hongpan-multi-positions-v1";
      let positions: any[] = [];
      try { positions = JSON.parse(localStorage.getItem(key) || "[]"); } catch {}
      const direction = asset === "futures" ? (side === "\u5f00\u7a7a" ? "short" : "long") : "long";
      const index = positions.findIndex((item) => item.asset === asset && item.quoteId === selected.quoteId && item.direction === direction);
      if (side === "\u5356\u51fa" || side === "\u5e73\u4ed3") {
        const closeIndex = side === "\u5e73\u4ed3" ? positions.findIndex((item) => item.asset === asset && item.quoteId === selected.quoteId) : index;
        if (closeIndex < 0 || positions[closeIndex].quantity < quantity) return setMessage("\u53ef\u5e73\u6301\u4ed3\u4e0d\u8db3");
        positions[closeIndex] = { ...positions[closeIndex], quantity: positions[closeIndex].quantity - quantity };
        positions = positions.filter((item) => item.quantity > 0);
      } else if (index >= 0) {
        const item = positions[index], total = item.cost * item.quantity + quote.price * quantity;
        positions[index] = { ...item, quantity: item.quantity + quantity, cost: total / (item.quantity + quantity) };
      } else positions.unshift({ id: crypto.randomUUID(), asset, quoteId: selected.quoteId, code: selected.code, name: selected.name, direction, quantity, cost: quote.price });
      localStorage.setItem(key, JSON.stringify(positions));
    }
    window.dispatchEvent(new Event("hongpan-portfolio-updated"));
    setMessage(`${side}\u59d4\u6258\u5df2\u6a21\u62df\u6210\u4ea4\uff1a${selected.name} ${quantity}${asset === "futures" ? "\u624b" : "\u5355\u4f4d"} @ ${quote.price}`);
  }

  return (
    <main className="market-terminal">
      <header className="terminal-head">
        <Link href="/" className="brand">红盘 <span>模拟交易所</span></Link>
        <nav>{(["stock", "etf", "fund", "futures", "bond"] as Asset[]).map((key) =>
          <Link key={key} href={`/market/${key}`} className={key === asset ? "active" : ""}>{configurations[key].label}</Link>
        )}</nav>
        <Link href="/" className="back-link">返回总览</Link>
      </header>

      <section className={`session-banner ${open ? "is-open" : "is-closed"}`}>
        <b>{open ? "交易中" : "休市中 · 下单已锁定"}</b>
        <span>北京时间 · {asset === "futures" ? "期货合约时段因交易所和品种而异" : "严格按境内交易所日间时段判断"} · 行情每 5 秒刷新</span>
      </section>

      <div className="terminal-layout">
        <aside className="finder market-panel">
          <div className="panel-title"><i>{config.icon}</i><div><b>{config.label} 产品中心</b><small>{config.example}</small></div></div>
          <form onSubmit={search}><input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder={config.placeholder} /><button>{loading ? "查询中" : "查询"}</button></form>
          <div className="result-list">
            {!keyword.trim() && recentSearches.length > 0 && <div className="recent-searches"><div className="recent-search-title">{"\u6700\u8fd1\u641c\u7d22"}<small>{"\u6700\u591a\u4fdd\u7559 5 \u6761"}</small></div>{recentSearches.map((term) => <div className="recent-search-row" key={term}><button type="button" onClick={() => setKeyword(term)}><span>{term}</span></button><button type="button" className="recent-search-delete" aria-label={`删除 ${term}`} onClick={() => deleteRecentSearch(term)}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>)}</div>}
            {!keyword.trim() && !recentSearches.length && <div className="hint">输入代码或名称，选择产品后即可查看行情、K 线和交易规则。</div>}
            {results.map((p) => <button key={p.quoteId} className={selected?.quoteId === p.quoteId ? "selected" : ""} onClick={() => { setSelected(p); rememberSearch(keyword || `${p.code} ${p.name}`); }}>
              <span><b>{p.name}</b><small>{p.code}</small></span><em>查看 ›</em>
            </button>)}
          </div>
        </aside>

        <section className="market-view">
          <div className="quote-card market-panel">
            {selected ? <>
              <div><small>{config.label} · {selected.code}</small><h1>{selected.name}</h1><button className={`watch-toggle ${watched ? "active" : ""}`} onClick={toggleWatch}>{watched ? "已关注 · 取消" : "+ 关注产品"}</button></div>
              <div className={`last-price ${(quote?.changePct || 0) >= 0 ? "rise" : "fall"}`}>
                <strong>{quote?.price ?? "—"}</strong><span>{quote?.noTrade ? "暂无成交 · 参考价" : quote ? `${quote.changePct >= 0 ? "+" : ""}${quote.changePct}%` : "等待行情"}</span>{quoteCheckedAt && <small className="quote-refresh">5 秒轮询 · 最近刷新 {quoteCheckedAt}</small>}
              </div>
              <dl><div><dt>今开</dt><dd>{quote?.open ?? "—"}</dd></div><div><dt>最高</dt><dd>{quote?.high ?? "—"}</dd></div><div><dt>最低</dt><dd>{quote?.low ?? "—"}</dd></div><div><dt>昨收</dt><dd>{quote?.prevClose ?? "—"}</dd></div></dl>
              {asset === "stock" && <dl className="quote-fundamentals"><div><dt>{"市值"}</dt><dd>{compactMarketValue(quote?.marketCap)}</dd></div><div><dt>{"换手率"}</dt><dd>{Number.isFinite(quote?.turnoverRate) ? quote!.turnoverRate!.toFixed(2) + "%" : "—"}</dd></div><div><dt>{"动态PE"}</dt><dd>{Number.isFinite(quote?.peDynamic) ? quote!.peDynamic!.toFixed(2) : "—"}</dd></div></dl>}
            </> : <div className="empty-product"><b>选择一个{config.label}产品</b><span>行情详情将在这里展开</span></div>}
          </div>
          <div className="chart-card market-panel">
            <div className="chart-head"><b>价格走势</b><div>{[["minute","分"],["hour","时"],["day","日"],["month","月"]].map(([key, label]) =>
              <button key={key} className={range === key ? "active" : ""} onClick={() => setRange(key)}>{label}</button>)}</div></div>
            <CandleChart bars={bars} loading={chartLoading} range={range} emptyText={asset==="bond"&&quote?.noTrade?"该债券暂无历史成交，当前仅展示发行/昨收参考价。":"暂无该周期数据"} />
            {chartCheckedAt && <div className="chart-refresh">走势图最近刷新 {chartCheckedAt} · 分时图同步最新报价</div>}
            <small className="source-note">{range === "since2025" && bars.length ? `已载入 ${bars[0].time} 至 ${bars[bars.length-1].time}，区间涨跌 ${((bars[bars.length-1].close/bars[0].close-1)*100).toFixed(2)}% · ` : ""}{asset !== "futures" ? "日间走势图严格截至 15:00 · " : ""}行情来源：公开市场数据接口；存在网络与供应商延迟，仅用于模拟学习。</small>
          </div>
        </section>

        <aside className="market-ticket market-panel">
          <div className="panel-title"><i>单</i><div><b>{config.label} 下单</b><small>{settlement} · {config.unit}</small></div></div>
          <label>当前产品<input readOnly value={selected ? `${selected.name} ${selected.code}` : "请先选择产品"} /></label>
          <label>{quote?.noTrade?"发行/昨收参考价":"模拟成交价"}<input readOnly value={quote?.price ?? "—"} /></label>
          <label>{"\u6570\u91cf\uff08\u80a1\uff09"}<div className="quantity-stepper"><button type="button" onClick={() => setQuantity(Math.max(0, quantity - (asset === "stock" ? 100 : 1)))}>{"−"}</button><input type="number" min="0" step={asset === "stock" ? 100 : 1} value={quantity || ""} placeholder={asset === "stock" ? "\u8bf7\u8f93\u5165 100 \u7684\u6574\u6570\u500d" : "\u8bf7\u8f93\u5165\u6570\u91cf"} onChange={(e) => setQuantity(e.target.value === "" ? 0 : Number(e.target.value))} /><button type="button" onClick={() => setQuantity(quantity + (asset === "stock" ? 100 : 1))}>+</button></div></label>{asset === "stock" && selected && <div className="held-shares">{"\u5f53\u524d\u6301\u4ed3\uff1a"}<b>{(() => { try { const s=JSON.parse(localStorage.getItem("hongpan-v3")||"null"); return s?.positions?.find((p:any)=>p.code===selected.code)?.shares||0; } catch { return 0; } })()} {"\u80a1"}</b></div>}
          {asset === "futures" ? <div className="order-grid"><button disabled={!open||!!quote?.noTrade} onClick={() => order("开多")}>开多</button><button disabled={!open||!!quote?.noTrade} onClick={() => order("开空")}>开空</button><button disabled={!open||!!quote?.noTrade} onClick={() => order("平仓")} className="neutral">平仓</button></div>
            : <div className="order-grid"><button disabled={!open||!!quote?.noTrade} onClick={() => order("买入")}>买入</button><button disabled={!open||!!quote?.noTrade} onClick={() => order("卖出")} className="sell">卖出</button></div>}
          {message && <div className="order-message">{message}</div>}
          <div className="rules"><b>真实规则模拟</b>{ruleNotes.map((note) => <p key={note}>• {note}</p>)}</div>
        </aside>
      </div>
    </main>
  );
}
